import { Link } from 'react-router-dom';
import {
  ArrowIcon, CheckIcon, CONTACT_EMAIL, DEFAULT_DESCRIPTION, DEFAULT_TITLE, JOB_PORTAL_URL, SITE_URL, Seo, SiteFooter,
  SiteHeader, useScrollToHash,
} from '../components/shared';

const STEPS = [
  { title: 'Apply', text: 'Your details, your city, and the equipment you own. Add a photo ID, a selfie and your student card.' },
  { title: 'Get approved', text: 'We review within two business days. Sign the contractor agreement and the job board unlocks.' },
  { title: 'Claim a job', text: 'Browse jobs near your postal code, see what each pays, and propose a time. The homeowner confirms.' },
  { title: 'Get paid', text: 'Take a before and after photo. Once the homeowner approves, the money lands in your account.' },
];

const WORK_TYPES = [
  'Lawn mowing and trimming',
  'Weeding and garden beds',
  'Leaf and yard cleanups',
  'Snow clearing in winter',
  'General home maintenance',
];

// `/register` renders this same page: it keeps its old title and description
// and points search engines at `/students` as the canonical copy.
export default function StudentsPage({ legacyRegister = false }) {
  useScrollToHash();

  return (
    <div className="page">
      {legacyRegister ? (
        <Seo title={DEFAULT_TITLE} description={DEFAULT_DESCRIPTION} canonical={`${SITE_URL}/students`} />
      ) : (
        <Seo
          title="Student Jobs in Lawn Care & Yard Work | Student For Hire"
          description="Get paid for lawn care, yard cleanups and snow clearing near you. Apply to work with Student For Hire."
        />
      )}

      <SiteHeader>
        <a href="#how" className="nav-link nav-link--wide">How it works</a>
        <a href="#work" className="nav-link nav-link--wide">The work</a>
        <Link to="/home-owners" className="nav-link">For home owners</Link>
        <a href={JOB_PORTAL_URL} className="btn btn--primary btn--header">Apply now</a>
      </SiteHeader>

      <main>
        <section className="hero hero--students">
          <img className="hero__img" src="/photos/student-mowing.jpg" alt="Student mowing a lawn in a Student For Hire shirt" width="1600" height="1066" loading="eager" />
          <div className="hero__scrim" />
          <div className="hero__inner">
            <div className="wrap">
              <div className="hero__copy">
                <div className="eyebrow">For students</div>
                <h1 className="h1">Work anytime.<br />Get paid.</h1>
                <p className="hero__lede">
                  Lawn care, yard cleanups and snow clearing for homeowners near you. You pick the jobs, you set the hours, the
                  money goes straight to your bank.
                </p>
                <div className="hero__actions">
                  <a href={JOB_PORTAL_URL} className="btn btn--primary btn--lg">Apply to work <ArrowIcon /></a>
                  <a href="#work" className="btn btn--ghost">See the work</a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="stat-bar" aria-label="Why work with Student For Hire">
          <div className="wrap">
            <div className="stat">
              <div className="stat__title">Paid per job</div>
              <div className="stat__text">Every job shows what it pays before you take it.</div>
            </div>
            <div className="stat">
              <div className="stat__title">Your own schedule</div>
              <div className="stat__text">Propose a time that works around your schedule.</div>
            </div>
            <div className="stat">
              <div className="stat__title">Jobs near you</div>
              <div className="stat__text">Filtered to your postal code, so no long trips.</div>
            </div>
          </div>
        </section>

        <section id="how" className="section">
          <div className="wrap">
            <h2 className="h2 steps-title">Four steps from application to payday.</h2>
            <div className="hairline-grid hairline-grid--4">
              {STEPS.map((s, i) => (
                <div key={s.title} className="hairline-grid__cell">
                  <div className="step-num">{String(i + 1).padStart(2, '0')}</div>
                  <h3 className="h3">{s.title}</h3>
                  <p>{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="work" className="section section--white">
          <div className="wrap work-grid">
            <div>
              <h2 className="h2">The work itself.</h2>
              <p className="work-grid__copy">
                Straightforward outdoor work. You bring your own tools, and you can take any job your equipment covers. Own a
                lawn mower and a weed whacker? Every lawn mowing job is open to you.
              </p>
              <ul className="checklist">
                {WORK_TYPES.map((w) => (
                  <li key={w}><CheckIcon />{w}</li>
                ))}
              </ul>
            </div>
            <div className="work-photos">
              <img className="work-photos__wide" src="/photos/striped-backyard.jpg" alt="Freshly striped backyard lawn" width="1600" height="1200" loading="lazy" />
              <img src="/photos/front-lawn-mowing.jpg" alt="Student mowing a front lawn" width="1600" height="1200" loading="lazy" />
              <img src="/photos/front-lawn-curb.jpg" alt="Front lawn mowed with yard waste bagged at the curb" width="1000" height="844" loading="lazy" />
            </div>
          </div>
        </section>

        <section className="section section--center">
          <div className="wrap">
            <h2 className="h2">Start earning this season.</h2>
            <p>Applications take about five minutes. You need to be a student, live in the area, and be able to get to the job.</p>
            <a href={JOB_PORTAL_URL} className="btn btn--primary btn--lg">Apply to work <ArrowIcon /></a>
            <p className="small-print">Questions? <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
          </div>
        </section>
      </main>

      <SiteFooter crossLink={{ to: '/home-owners', label: 'For home owners' }} />
    </div>
  );
}
